import { ApiType } from '../ApiClient';
import { ApiPaths } from '../ApiPaths';
import {
  ComputeRouteRequest,
  ComputeRouteResponse,
  BackendTravelMode,
  RouteData,
} from '../../../../../types/mapsApiTypes';
import { TravelMode } from '../../../../../types/TravelSuggestionType';
import { apiClient } from '../ApiClientSingleton';
import { retryWithBackoff, shouldRetryHttpError } from '../../../utils/maps/retryWithBackoff';

/**
 * Maps frontend TravelMode enum to backend BackendTravelMode
 */
export const mapTravelModeToBackend = (travelMode: TravelMode): BackendTravelMode => {
  switch (travelMode) {
    case TravelMode.CAR:
    case TravelMode.MC:
      return 'drive';
    case TravelMode.BIKE:
      return 'bicycle';
    case TravelMode.WALK:
      return 'walk';
    default:
      return 'drive';
  }
};

/**
 * Converts backend duration string (TimeSpan) to seconds
 * Supports formats like "01:30:00" or "PT1H30M"
 */
const parseDuration = (duration: string): number => {
  // Handle TimeSpan format "HH:MM:SS"
  if (duration.includes(':')) {
    const parts = duration.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    return hours * 3600 + minutes * 60 + seconds;
  }

  // Handle ISO 8601 duration format "PT1H30M"
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.match(regex);
  if (matches) {
    const hours = parseInt(matches[1] || '0', 10);
    const minutes = parseInt(matches[2] || '0', 10);
    const seconds = parseInt(matches[3] || '0', 10);
    return hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
};

/**
 * Formats seconds to human-readable duration text
 * @param seconds Total seconds
 * @returns Formatted string like "1 time 30 min"
 */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return `${hours} time ${minutes} min`;
  }
  if (hours > 0) {
    return `${hours} time`;
  }
  return `${minutes} min`;
};

/**
 * Formats meters to human-readable distance text
 * @param meters Distance in meters
 * @returns Formatted string like "42.5 km" or "850 m"
 */
const formatDistance = (meters: number): string => {
  if (meters >= 1000) {
    const km = (meters / 1000).toFixed(1);
    return `${km} km`;
  }
  return `${meters} m`;
};

/**
 * Computes route using NAF backend Maps API with retry logic
 * Retries up to 3 times on transient failures (network errors, 5xx server errors)
 * with exponential backoff (1s, 2s, 4s)
 *
 * Note: Error tracking is handled automatically by ApiClient base class
 *
 * @param request Route computation request
 * @returns Normalized route data
 * @throws Error if API call fails after all retries
 */
export const computeRoute = async (request: ComputeRouteRequest): Promise<RouteData> => {
  try {
    return await retryWithBackoff(
      async () => {
        const response = await apiClient.post<ComputeRouteResponse>(ApiType.apimMapsApi, {
          url: ApiPaths[ApiType.apimMapsApi].routes.compute,
          data: request,
        });

        const { data } = response;

        // Parse duration from backend response
        const durationSeconds = parseDuration(data.duration);

        // Normalize response for internal use
        const normalized: RouteData = {
          polyline: data.polyline,
          bounds: data.bounds,
          distance: {
            meters: data.distanceMeters,
            text: formatDistance(data.distanceMeters),
          },
          duration: {
            seconds: durationSeconds,
            text: formatDuration(durationSeconds),
          },
          legs: data.legs?.map((leg) => ({
            distanceMeters: leg.distanceMeters,
            durationSeconds: parseDuration(leg.duration),
          })),
          warnings: data.warnings,
        };

        return normalized;
      },
      {
        maxRetries: 3,
        initialDelayMs: 1000,
        backoffMultiplier: 2,
        shouldRetry: shouldRetryHttpError,
        onRetry: (error, attempt, delayMs) => {
          // eslint-disable-next-line no-console
          console.warn(`Retrying route computation (attempt ${attempt + 1}) after ${delayMs}ms:`, error.message);
        },
      },
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to compute route after retries:', error);
    throw error;
  }
};
